import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import swal from "sweetalert";

import i18n from "../../../../../i18n";
import { createBankAccount, getAllBankAccounts } from "../../../../../models/bank_accounts";


/**
 * BankAccountModal
 *
 * Lets the user pick a previously saved bank account, or add a new one.
 * On selecting/adding an account, calls onSelect(account) so the parent
 * (Wallet.jsx withdraw form) can auto-fill bank / beneficiary / account_number.
 *
 * Props:
 *  show      - boolean, controls modal visibility
 *  onHide    - fn, called to close the modal
 *  onSelect  - fn(account), called with the chosen account object
 */
export const BankAccountModal = ({ onHide, onSelect }) => {
  const [view, setView] = useState("list"); // "list" | "add"
  const [is_loading, setIsLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [is_saving, setIsSaving] = useState(false);
  const [form, setForm] = useState({
    beneficiary: "",
    bank: "",
    account_number: "",
    iban: "",
  });
 
  useEffect(() => {
    loadAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
 
  const loadAccounts = async () => {
    setIsLoading(true);
    try {
      const response = await getAllBankAccounts();
      if (response && response.success) {
        let list = response.data || [];
        setAccounts(list);
        let defaultAcc = list.find((a) => a.is_default);
        setSelectedId(defaultAcc ? defaultAcc.id : list[0] ? list[0].id : null);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };
 
  const maskAccountNumber = (number) => {
    if (!number) return "";
    const str = String(number);
    if (str.length <= 7) return str;
    return str.slice(0, 3) + "x".repeat(str.length - 7) + str.slice(-4);
  };
 
  const getInitial = (name) => (name ? name.trim().charAt(0).toUpperCase() : "?");
 
  const handleFormChange = (event) => {
    let updated = { ...form };
    updated[event.target.name] = event.target.value;
    setForm(updated);
  };
 
  const handleChooseAccount = () => {
    const account = accounts.find((a) => a.id === selectedId);
    if (!account) {
      swal(i18n.t("Warning"), "Please select an account", "warning");
      return;
    }
    onSelect(account);
    onHide();
  };
 
  const handleAddAccount = async (event) => {
    event.preventDefault();
    if (!form.beneficiary || !form.bank || !form.account_number) {
      swal(i18n.t("Warning"), "Please fill all required fields", "warning");
      return;
    }
    setIsSaving(true);
    try {
      const response = await createBankAccount(form);
      if (response && response.success) {
        // Use the newly created account immediately - no need to make
        // the user go back to the list and select it again.
        onSelect(response.data);
        onHide();
      } else {
        swal("Failed", response?.message || "Could not save account", "error");
      }
    } catch (err) {
      console.log(err);
      swal("Failed", "Could not save account", "error");
    } finally {
      setIsSaving(false);
    }
  };
 
  return (
    <div className="wal-bank-panel">
      {view === "list" && (
        <div className="wal-bank-list">
          <div className="wal-bank-panel__head">
            <div className="wal-bank-eyebrow">Main Account</div>
            <button type="button" className="wal-bank-cancel" onClick={onHide}>
              Cancel
            </button>
          </div>
 
          {is_loading && (
            <React.Fragment>
              {[1, 2].map((n) => (
                <div className="wal-skel-bank-card" key={n}>
                  <span className="wal-skel-bank-card__icon" />
                  <div className="wal-skel-bank-card__lines">
                    <span className="wal-skel-bank-card__line" />
                    <span className="wal-skel-bank-card__line" />
                    <span className="wal-skel-bank-card__line" />
                  </div>
                </div>
              ))}
            </React.Fragment>
          )}
 
          {!is_loading && accounts.length === 0 && (
            <div className="wal-bank-empty">
              You don't have any saved accounts yet.
            </div>
          )}
 
          {!is_loading &&
            accounts.map((account) => {
              const selected = selectedId === account.id;
              return (
                <div
                  key={account.id}
                  className={
                    "wal-bank-card" + (selected ? " wal-bank-card--selected" : "")
                  }
                  onClick={() => setSelectedId(account.id)}
                >
                  <div className="wal-bank-card__left">
                    <div className="wal-bank-card__icon">
                      {getInitial(account.beneficiary)}
                    </div>
                    <div>
                      <div className="wal-bank-card__name">
                        {account.beneficiary}
                      </div>
                      <div className="wal-bank-card__bank">{account.bank}</div>
                      <div className="wal-bank-card__number">
                        {maskAccountNumber(account.account_number)}
                      </div>
                    </div>
                  </div>
                  <div
                    className={
                      "wal-bank-card__mark" + (selected ? " wal-bank-card__mark--on" : "")
                    }
                  >
                    {selected && <i className="fa fa-check" />}
                  </div>
                </div>
              );
            })}
 
          {/* same dashed-button style used for "Use Saved Account" in the withdraw modal */}
          <button
            type="button"
            className="wal-wd-saved-btn"
            onClick={() => setView("add")}
          >
            <i className="fa fa-plus" />
            Add Another Account
          </button>
 
          {/* same solid submit-button style used in the withdraw modal */}
          <button
            type="button"
            className="wal-wd-submit"
            disabled={accounts.length === 0}
            onClick={handleChooseAccount}
          >
            Choose Account
          </button>
        </div>
      )}
 
      {view === "add" && (
        <div>
          <div className="wal-bank-panel__head">
            <div className="wal-bank-eyebrow">Add New Account</div>
            <button type="button" className="wal-bank-cancel" onClick={onHide}>
              Cancel
            </button>
          </div>
 
          <form onSubmit={handleAddAccount} autoComplete="off">
            <div className="wal-wd-field">
              <label>
                Account Holder Name<span className="wal-required">*</span>
              </label>
              <input
                type="text"
                name="beneficiary"
                value={form.beneficiary}
                onChange={handleFormChange}
              />
            </div>
            <div className="wal-wd-field">
              <label>
                {i18n.t("bank")}<span className="wal-required">*</span>
              </label>
              <input
                type="text"
                name="bank"
                value={form.bank}
                onChange={handleFormChange}
              />
            </div>
            <div className="wal-wd-field">
              <label>
                {i18n.t("account_number")}<span className="wal-required">*</span>
              </label>
              <input
                type="text"
                name="account_number"
                value={form.account_number}
                onChange={handleFormChange}
              />
            </div>
            <div className="wal-wd-field">
              <label>IBAN Number</label>
              <input
                type="text"
                name="iban"
                value={form.iban}
                onChange={handleFormChange}
              />
            </div>
 
            <div className="wal-bank-form-actions">
              <button
                type="button"
                className="wal-bank-btn-back"
                onClick={() => setView("list")}
                disabled={is_saving}
              >
                Back
              </button>
              <button
                type="submit"
                className="wal-wd-submit"
                disabled={is_saving}
              >
                {is_saving ? (
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                ) : (
                  "Save Account"
                )}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};